/**
 * @file main.jsx
 * @description React application bootstrap.
 *
 * Official @azure/msal-react 2.x initialization pattern:
 *  1. Import the shared PCA instance (auth/msalInstance.js).
 *  2. await msalInstance.initialize() — MSAL must be fully ready before any
 *     component or hook runs. Top-level await works here because Vite treats
 *     .jsx files as ES modules.
 *  3. await msalInstance.handleRedirectPromise() — processes the Entra ID
 *     redirect URL parameters after a login/logout redirect, resolving the
 *     tokens and cleaning the URL before React mounts.
 *  4. Set the active account from the redirect response or the MSAL cache
 *     (covers page refreshes and repeat visits).
 *  5. Register an event callback so the active account stays in sync after
 *     any future interactive logins during the session.
 *  6. Render the app, wrapping with MsalProvider and passing the fully-
 *     initialized instance.
 *
 * Import order for styles matters:
 *  1. custom.scss — Bootstrap variable overrides + full Bootstrap import
 *  2. theme.css   — Application CSS custom properties (design tokens)
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';
import { EventType } from '@azure/msal-browser';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';

import './styles/custom.scss';
import './styles/theme.css';
// Bootstrap Icons CSS is imported here (not in SCSS) so Vite can resolve the
// font file URLs through its asset pipeline rather than through SCSS processing.
import 'bootstrap-icons/font/bootstrap-icons.css';

import msalInstance from './auth/msalInstance';
import App from './App';

// ── Step 1: Initialize MSAL before rendering ─────────────────────────────────
// MSAL loads its token cache and configures itself. Must be awaited so that
// getAllAccounts(), getActiveAccount(), and acquireTokenSilent() are safe to call.
await msalInstance.initialize();

// ── Step 2: Handle any pending redirect response ──────────────────────────────
// After an Entra ID login redirect, the browser returns here with tokens in
// the URL hash/query. handleRedirectPromise() processes them, clears the URL,
// and returns the AuthenticationResult (or null if no redirect is in progress).
//
// This MUST be wrapped in try/catch: if the redirect carries an auth failure
// (e.g. user cancelled, AADSTS error), the promise rejects. Since this is a
// top-level await, an uncaught rejection would throw the whole module and
// ReactDOM.createRoot().render() below would never run — the app would be
// stuck on a blank page, and MSAL's "interaction in progress" storage flag
// could be left set, causing every subsequent login attempt to fail with
// `interaction_in_progress` until the flag is cleared.
let redirectResponse = null;
try {
  redirectResponse = await msalInstance.handleRedirectPromise();
} catch (err) {
  console.error('[Auth] Redirect handling failed:', err);
  // Defensive cleanup: MSAL should clear its own interaction-status flag even
  // on error, but if this module crashed on a previous load before reaching
  // this point, a stale flag can linger and block the next login attempt.
  Object.keys(sessionStorage)
    .filter((key) => key.endsWith('.interaction.status'))
    .forEach((key) => sessionStorage.removeItem(key));
}

if (redirectResponse?.account) {
  // Redirect just completed — the returned account becomes active
  msalInstance.setActiveAccount(redirectResponse.account);
} else {
  // No redirect in progress: restore the active account from the MSAL cache.
  // If only one account is cached (the common case) activate it automatically.
  const cachedAccounts = msalInstance.getAllAccounts();
  if (cachedAccounts.length === 1) {
    msalInstance.setActiveAccount(cachedAccounts[0]);
  }
}

// ── Step 3: Keep the active account in sync for the rest of the session ───────
// Event callbacks fire on interactive logins (popup, redirect, SSO silent).
// Without this, acquireTokenSilent() could fail after a mid-session re-auth.
msalInstance.addEventCallback((event) => {
  if (
    (
      event.eventType === EventType.LOGIN_SUCCESS ||
      event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
      event.eventType === EventType.SSO_SILENT_SUCCESS
    ) &&
    event.payload?.account
  ) {
    msalInstance.setActiveAccount(event.payload.account);
  }
});

// ── Step 4: React Query client ────────────────────────────────────────────────
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// ── Step 5: Render — MSAL is fully initialised at this point ─────────────────
ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    {/*
      MsalProvider receives the already-initialized instance.
      All @azure/msal-react hooks (useMsal, useIsAuthenticated, etc.)
      are available to any component inside this tree.
    */}
    <MsalProvider instance={msalInstance}>
      <QueryClientProvider client={queryClient}>
        {/*
          future flags suppress React Router v6 → v7 upgrade warnings.
          v7_startTransition: wraps state updates in React.startTransition.
          v7_relativeSplatPath: normalises relative paths inside splat routes.
        */}
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </MsalProvider>
  </React.StrictMode>
);
